import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import {
  Eye,
  Download,
  Trash2,
  Shield,
  FileText
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export function PrivacySettings() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const handleDataExport = async () => {
    setIsLoading(true);
    try {
      // Implementation for data export
      toast({
        title: "Export Requested",
        description: "Your data export will be emailed to you when ready.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to initiate data export.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Profile Visibility */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Eye className="w-5 h-5" />
          Profile Visibility
        </h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <p className="font-medium">Public Profile</p>
              <p className="text-sm text-muted-foreground">
                Make your profile visible to other users
              </p>
            </div>
            <Switch defaultChecked />
          </div>
          <Select defaultValue="connections">
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder="Who can see my profile" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="everyone">Everyone</SelectItem>
              <SelectItem value="connections">Connections Only</SelectItem>
              <SelectItem value="private">Private</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Data Management */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Shield className="w-5 h-5" />
          Data Management
        </h2>
        <div className="space-y-4">
          <Button 
            variant="outline" 
            onClick={handleDataExport}
            disabled={isLoading}
            className="w-full sm:w-auto"
          >
            <Download className="w-4 h-4 mr-2" />
            Export My Data
          </Button>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <p className="font-medium">Data Analytics</p>
              <p className="text-sm text-muted-foreground">
                Allow anonymous usage data collection
              </p>
            </div>
            <Switch defaultChecked />
          </div>
        </div>
      </div>

      {/* HIPAA Compliance */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <FileText className="w-5 h-5" />
          HIPAA Compliance
        </h2>
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Your data is protected in accordance with HIPAA regulations
          </p>
          <Button variant="link" className="p-0 h-auto">
            View Privacy Policy
          </Button>
        </div>
      </div>

      {/* Account Deletion */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold flex items-center gap-2 text-destructive">
          <Trash2 className="w-5 h-5" />
          Delete Account
        </h2>
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Permanently delete your account and all associated data
          </p>
          <Button variant="destructive">
            Delete Account
          </Button>
        </div>
      </div>
    </div>
  );
} 